import ExclamationCircleOutlined from '@ant-design/icons/lib/icons/ExclamationCircleOutlined';
import {isArray} from '@apollo/client/cache/inmemory/helpers';
import {Button, Modal, Table} from 'antd';
import {CardProps} from 'antd/es/card';
import {ColumnProps} from 'antd/lib/table';
import {
  Account,
  AccountFieldsFragment,
  Maybe,
  PibisiAction,
  PibisiAlertStatus,
  PibisiMatchStatus,
  useResolvePibisiAlertMutation,
  useResolvePibisiMatchMutation
} from 'api/generated';
import {Spacer, Stack} from 'components';
import {StatusTag} from 'components/StatusTag';
import {TableCard} from 'components/TableCard';
import {useAccessLevel} from 'hooks/useAccessLevel';
import humanize from 'humanize-string';
import {colors} from 'lib/colors';
import {capitalize} from 'lib/utils';
import React, {useState} from 'react';
import {Alert} from 'routes/Account/Pibisi/Alert';
import {Match} from 'routes/Account/Pibisi/Match';
import {getFullName} from 'routes/Account/utils';
import {PibisiStatusTag} from './PibisiStatusTag';
import {LegalTypes, PibisiAlert, PibisiMatch} from './types';

export type Props = {
  account?: Maybe<AccountFieldsFragment>;
  cardProps: CardProps;
};

const getPibisiAlerts = (account: Account) => {
  const alerts: PibisiAlert[] = [];
  const business = account.business;
  const representatives = account.representatives;
  const businessPibisi = business?.pibisi;
  if (business && businessPibisi && businessPibisi.alerts) {
    businessPibisi.alerts.forEach((alert) => {
      alerts.push({
        ...alert!,
        customerId: businessPibisi.customerId!,
        legalName: business.legalName!,
        legalType: LegalTypes.BUSINESS
      });
    });
  }

  if (representatives) {
    representatives.forEach((rep) => {
      const repPibisi = rep.pibisi;
      if (repPibisi && repPibisi.alerts) {
        repPibisi.alerts.forEach((alert) => {
          alerts.push({
            ...alert!,
            customerId: repPibisi.customerId!,
            legalName: getFullName(rep),
            legalType: LegalTypes.INDIVIDUAL
          });
        });
      }
    });
  }

  return alerts;
};

export const getPibisiMatches = (account: Account) => {
  const matches: PibisiMatch[] = [];
  const business = account.business;
  const representatives = account.representatives;
  const businessPibisi = business?.pibisi;
  if (business && businessPibisi && businessPibisi.matches) {
    businessPibisi.matches.forEach((match) => {
      matches.push({
        ...match!,
        customerId: businessPibisi.customerId!,
        legalName: business.legalName!,
        legalType: LegalTypes.BUSINESS
      });
    });
  }

  if (representatives) {
    representatives.forEach((rep) => {
      const repPibisi = rep.pibisi;
      if (repPibisi && repPibisi.matches) {
        repPibisi.matches.forEach((match) => {
          matches.push({
            ...match!,
            customerId: repPibisi.customerId!,
            legalName: getFullName(rep),
            legalType: LegalTypes.INDIVIDUAL
          });
        });
      }
    });
  }

  return matches;
};

export const Pibisi: React.FC<Props> = ({account, cardProps}) => {
  const {isAdmin} = useAccessLevel();

  const [resolvePibisiAlert] = useResolvePibisiAlertMutation();
  const [resolvePibisiMatch] = useResolvePibisiMatchMutation();

  const [selectedAlert, setSelectedAlert] = useState<PibisiAlert | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<PibisiMatch | null>(null);

  if (!isAdmin || !account) return null;

  const alerts = getPibisiAlerts(account);
  const matches = getPibisiMatches(account);

  console.log({alerts, matches});
  const resolveAlert = async (alert: PibisiAlert, action: PibisiAction) => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: `Are you sure?`,
      content:
        action === PibisiAction.REJECT
          ? `You confirm that this alert is false and you want to reject it`
          : `You confirm that this alert is true and you want to accept it`,
      okText: `${capitalize(action)} alert`,
      async onOk() {
        await resolvePibisiAlert({
          variables: {
            input: {
              accountId: account.id,
              customerId: alert.customerId,
              alertId: alert.id,
              action
            }
          }
        });
        setSelectedAlert(null);
      }
    });
  };

  const resolveMatch = async (match: PibisiMatch, action: PibisiAction) => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: `Are you sure?`,
      content:
        action === PibisiAction.REJECT
          ? `You confirm that this match is false and you want to reject it`
          : `You confirm that this match is true and you want to accept it`,
      okText: `${capitalize(action)} match`,
      async onOk() {
        await resolvePibisiMatch({
          variables: {
            input: {
              accountId: account.id,
              customerId: match.customerId,
              matchId: match.id,
              action
            }
          }
        });
        setSelectedMatch(null);
      }
    });
  };

  const alertsColumns: Array<ColumnProps<PibisiAlert>> = [
    {
      title: 'Legal type',
      dataIndex: 'legalType',
      className: 'minimized-cell',
      render: (legalType) => <StatusTag>{legalType}</StatusTag>
    },
    {
      title: 'Legal name',
      dataIndex: 'legalName',
      className: 'clickable',
      onCell: (alert) => ({
        onClick: (e: any) => {
          e.preventDefault();
          setSelectedAlert(alert);
        }
      })
    },
    {
      title: 'Alert type',
      dataIndex: 'type',
      className: 'minimized-cell',
      render: (type) => <StatusTag color={colors.error}>{humanize(type || '')}</StatusTag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'minimized-cell',
      render: (status) => <PibisiStatusTag status={status} />
    }
  ];

  const matchesColumns: Array<ColumnProps<PibisiMatch>> = [
    {
      title: 'Legal type',
      dataIndex: 'legalType',
      className: 'minimized-cell',
      render: (legalType) => <StatusTag>{legalType}</StatusTag>
    },
    {
      title: 'Legal name',
      dataIndex: 'legalName',
      className: 'clickable',
      onCell: (match) => ({
        onClick: (e: any) => {
          e.preventDefault();
          setSelectedMatch(match);
        }
      })
    },
    {
      title: 'Match type',
      dataIndex: 'type',
      className: 'minimized-cell',
      render: (_, match) => {
        const type = Object.keys(match).find((key) => isArray(match[key as keyof PibisiMatch]));
        return <StatusTag color={colors.error}>{humanize(type || '')}</StatusTag>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'minimized-cell',
      render: (status) => <PibisiStatusTag status={status} />
    }
  ];

  return (
    <>
      <TableCard title="PIBISI Alerts" {...cardProps}>
        <Table dataSource={alerts} columns={alertsColumns as any} rowKey="id" pagination={false} />
      </TableCard>
      <TableCard title="PIBISI Matches" {...cardProps}>
        <Table
          dataSource={matches}
          columns={matchesColumns as any}
          rowKey="id"
          pagination={false}
        />
      </TableCard>
      <Modal
        title={selectedAlert?.legalName}
        width={800}
        open={Boolean(selectedAlert)}
        onCancel={() => setSelectedAlert(null)}
        footer={
          selectedAlert?.status === PibisiAlertStatus.PENDING ? (
            <Stack>
              <Button onClick={() => setSelectedAlert(null)}>Cancel</Button>
              <Spacer />
              <Button onClick={() => resolveAlert(selectedAlert, PibisiAction.REJECT)}>
                Reject alert
              </Button>
              <Button onClick={() => resolveAlert(selectedAlert, PibisiAction.ACCEPT)}>
                Accept alert
              </Button>
            </Stack>
          ) : null
        }>
        <Alert alert={selectedAlert} />
      </Modal>
      <Modal
        title={selectedMatch?.legalName}
        width={800}
        open={Boolean(selectedMatch)}
        onCancel={() => setSelectedMatch(null)}
        footer={
          selectedMatch?.status === PibisiMatchStatus.PENDING ? (
            <Stack>
              <Button onClick={() => setSelectedMatch(null)}>Cancel</Button>
              <Spacer />
              <Button onClick={() => resolveMatch(selectedMatch, PibisiAction.REJECT)}>
                Reject match
              </Button>
              <Button onClick={() => resolveMatch(selectedMatch, PibisiAction.ACCEPT)}>
                Accept match
              </Button>
            </Stack>
          ) : null
        }>
        <Match match={selectedMatch} />
      </Modal>
    </>
  );
};
